import React, { useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MOBILE_NAV } from '@/constants/testIds';
import {
  Sparkles, Compass, Mic2, Calendar, Briefcase, User,
  LayoutDashboard, ShieldCheck, UserCircle, LogOut, LogIn, UserPlus, Gift, BadgeCheck, Plus, X, Search,
} from 'lucide-react';

const TABS = [
  { id: 'discover', to: '/', label: 'Discover', icon: Compass, testId: MOBILE_NAV.discoverTab, end: true },
  { id: 'talent', to: '/artists', label: 'Talents', icon: Mic2, testId: MOBILE_NAV.talentTab },
  { id: 'events', to: '/events', label: 'Events', icon: Calendar, testId: MOBILE_NAV.eventsTab },
  { id: 'castings', to: '/castings', label: 'Castings', icon: Briefcase, testId: MOBILE_NAV.castingsTab },
];

const SheetItem = ({ icon: Icon, label, onClick, testId, danger = false, accent = false }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors ${
      danger
        ? 'border-rose-500/30 text-rose-300 hover:bg-rose-500/10'
        : accent
        ? 'border-fuchsia-400/40 text-fuchsia-100 bg-fuchsia-500/5 hover:bg-fuchsia-500/10'
        : 'border-white/10 text-slate-200 hover:bg-white/5'
    }`}
  >
    <Icon className={`h-4 w-4 ${accent ? 'text-fuchsia-300' : danger ? 'text-rose-300' : 'text-slate-400'}`} />
    <span className="text-sm font-medium">{label}</span>
  </button>
);

const AccountSheet = ({ user, open, onOpenChange, onLogout }) => {
  const navigate = useNavigate();
  const initials = (user?.name || 'U')
    .split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const go = (path) => () => { onOpenChange(false); navigate(path); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid={MOBILE_NAV.accountSheet}
        className="bg-[#0B1020] border-t border-white/10 text-slate-200 max-h-[85vh] overflow-y-auto rounded-t-3xl pb-[calc(env(safe-area-inset-bottom)+1.25rem)]"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="text-white font-display text-xl flex items-center gap-3">            {user ? (
              <>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user.picture || undefined} alt={user.name} />
                  <AvatarFallback className="bg-gradient-to-br from-fuchsia-500 to-purple-700 text-white text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="flex flex-col leading-tight">
                  <span className="truncate max-w-[200px]">{user.name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-normal">{user.email}</span>
                </span>
              </>
            ) : (
              <>
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-700 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <span>Join Maarivil</span>
              </>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Quick access to your account, dashboard and Maarivil tools.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-2">
          {!user && (
            <>
              <SheetItem icon={LogIn} label="Sign in" onClick={go('/login')} testId={MOBILE_NAV.sheetLogin} />
              <SheetItem icon={UserPlus} label="Create account" onClick={go('/register')} testId={MOBILE_NAV.sheetRegister} accent />
            </>
          )}
          {user && (
            <>
              <SheetItem icon={LayoutDashboard} label="Dashboard" onClick={go('/dashboard')} testId={MOBILE_NAV.sheetDashboard} />
              <SheetItem icon={Gift} label="Invite & Earn" onClick={go('/invite')} testId={MOBILE_NAV.sheetInvite} accent />
              <SheetItem icon={UserCircle} label="Artist Profile" onClick={go('/onboarding')} testId={MOBILE_NAV.sheetOnboarding} />
              <SheetItem icon={Sparkles} label="Post Casting" onClick={go('/post/casting')} testId={MOBILE_NAV.sheetPostCasting} />
              <SheetItem icon={Sparkles} label="Post Event" onClick={go('/post/event')} testId={MOBILE_NAV.sheetPostEvent} />
              <SheetItem icon={ShieldCheck} label="KYC Verification" onClick={go('/kyc')} testId={MOBILE_NAV.sheetKyc} />
              {user.role === 'admin' && (
                <SheetItem icon={ShieldCheck} label="Admin" onClick={go('/admin')} testId={MOBILE_NAV.sheetAdmin} />
              )}
              <SheetItem
                icon={LogOut}
                label="Sign out"
                onClick={() => { onOpenChange(false); onLogout(); }}
                testId={MOBILE_NAV.sheetLogout}
                danger
              />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const MobileBottomNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Hide the bottom nav on the OAuth callback hash flow
  if (location.hash?.includes('session_id=')) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const accountActive = ['/dashboard', '/invite', '/onboarding', '/post', '/kyc', '/admin', '/login', '/register', '/pricing']
    .some((p) => location.pathname.startsWith(p));

  return (
    <>
      <nav
        data-testid={MOBILE_NAV.root}
        className="md:hidden fixed bottom-0 inset-x-0 z-50 glass-strong border-t border-white/10 pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid grid-cols-5">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.id}
                to={t.to}
                end={t.end}
                data-testid={t.testId}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] uppercase tracking-wider transition-colors ${
                    isActive ? 'text-fuchsia-200' : 'text-slate-400 hover:text-slate-200'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute top-0 h-0.5 w-10 rounded-full bg-gradient-to-r from-fuchsia-400 via-purple-400 to-cyan-400" />
                    )}
                    <Icon className={`h-5 w-5 ${isActive ? 'drop-shadow-[0_0_8px_rgba(217,70,239,0.6)]' : ''}`} />
                    <span className="font-medium tracking-[0.08em]">{t.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
          <button
            type="button"
            data-testid={MOBILE_NAV.accountTab}
            onClick={() => setSheetOpen(true)}
            className={`relative flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] uppercase tracking-wider transition-colors ${
              accountActive || sheetOpen ? 'text-fuchsia-200' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {(accountActive || sheetOpen) && (
              <span className="absolute top-0 h-0.5 w-10 rounded-full bg-gradient-to-r from-fuchsia-400 via-purple-400 to-cyan-400" />
            )}
            {user ? (
              <Avatar className="h-5 w-5">
                <AvatarImage src={user.picture || undefined} alt="" />
                <AvatarFallback className="bg-gradient-to-br from-fuchsia-500 to-purple-700 text-white text-[8px]">
                  {(user.name || 'U').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            ) : (
              <User className={`h-5 w-5 ${accountActive || sheetOpen ? 'drop-shadow-[0_0_8px_rgba(217,70,239,0.6)]' : ''}`} />
            )}
            <span className="font-medium tracking-[0.08em]">{user ? 'You' : 'Account'}</span>
          </button>
        </div>
      </nav>
      <QuickActionsFab user={user} />
      <AccountSheet user={user} open={sheetOpen} onOpenChange={setSheetOpen} onLogout={handleLogout} />
    </>
  );
};

// Floating Instagram/TikTok-style "+" FAB hovering above the bottom-nav center.
// Tapping it reveals quick-action chips for posting / discovery.
const QuickActionsFab = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Auto-close on route change
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  if (location.hash?.includes('session_id=')) return null;

  const actions = user
    ? [
        { id: 'post-event', label: 'Post Event', icon: Calendar, to: '/post/event', color: 'from-cyan-500 to-blue-700', testId: MOBILE_NAV.fabPostEvent },
        { id: 'post-casting', label: 'Post Casting', icon: Briefcase, to: '/post/casting', color: 'from-emerald-500 to-teal-700', testId: MOBILE_NAV.fabPostCasting },
        { id: 'quick-book', label: 'Quick Book', icon: Search, to: '/artists', color: 'from-fuchsia-500 to-purple-700', testId: MOBILE_NAV.fabQuickBook },
        { id: 'invite', label: 'Invite & Earn', icon: Gift, to: '/invite', color: 'from-amber-400 to-orange-600', testId: MOBILE_NAV.fabInvite },
      ]
    : [
        { id: 'sign-in', label: 'Sign in', icon: LogIn, to: '/login', color: 'from-fuchsia-500 to-purple-700', testId: MOBILE_NAV.fabSignIn },
        { id: 'register', label: 'Create account', icon: UserPlus, to: '/register', color: 'from-cyan-500 to-blue-700', testId: MOBILE_NAV.fabRegister },
        { id: 'quick-book', label: 'Browse talent', icon: Search, to: '/artists', color: 'from-amber-400 to-orange-600', testId: MOBILE_NAV.fabQuickBook },
      ];

  const go = (to) => () => { setOpen(false); navigate(to); };

  return (
    <div className="md:hidden">
      {/* Backdrop */}
      {open && (
        <button
          type="button"
          aria-label="Close quick actions"
          data-testid={MOBILE_NAV.fabBackdrop}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm animate-in fade-in"
        />
      )}

      {/* Action chips — appear stacked above the FAB when open */}
      <div
        className={`fixed left-1/2 -translate-x-1/2 z-[56] flex flex-col items-center gap-3 transition-all duration-300 ease-out ${
          open ? 'bottom-32 opacity-100 pointer-events-auto' : 'bottom-20 opacity-0 pointer-events-none'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {actions.map((a, i) => {
          const Icon = a.icon;
          return (
            <button
              key={a.id}
              type="button"
              data-testid={a.testId}
              onClick={go(a.to)}
              style={{ transitionDelay: open ? `${i * 40}ms` : '0ms' }}
              className={`group flex items-center gap-3 pl-3 pr-5 py-2.5 rounded-full glass-strong border border-white/15 text-slate-100 text-sm font-medium shadow-2xl hover:border-fuchsia-400/40 transition-all whitespace-nowrap ${
                open ? 'translate-y-0' : 'translate-y-4'
              }`}
            >
              <span className={`h-8 w-8 rounded-full bg-gradient-to-br ${a.color} flex items-center justify-center shrink-0 shadow-lg`}>
                <Icon className="h-4 w-4 text-white" />
              </span>
              {a.label}
            </button>
          );
        })}
      </div>

      {/* The "+" FAB itself — sits above center of bottom nav */}
      <button
        type="button"
        data-testid={MOBILE_NAV.fab}
        aria-label={open ? 'Close quick actions' : 'Open quick actions'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        className={`fixed bottom-12 left-1/2 -translate-x-1/2 z-[57] h-14 w-14 rounded-full btn-rainbow text-white shadow-2xl shadow-fuchsia-500/40 ring-4 ring-[#0B1020] flex items-center justify-center transition-transform duration-300 ${
          open ? 'rotate-45 scale-110' : 'rotate-0 scale-100'
        }`}
      >
        {open ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
      </button>
    </div>
  );
};

export default MobileBottomNav;

// Floating "Join" CTA for logged-out visitors so they don't lose the primary conversion
// button after the hamburger is removed. Lives in the top-right safe corner on mobile.
export const MobileTopRightCta = () => {
  const { user } = useAuth();
  if (user) return null;
  return (
    <Link
      to="/register"
      data-testid={MOBILE_NAV.topJoinButton}
      className="md:hidden btn-rainbow text-white text-xs font-medium rounded-full px-3.5 py-1.5"
    >
      Join
    </Link>
  );
};
